// src/ProfilesDisplayShareCredits.jsx
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { AppContext } from './AppContext';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
} from 'firebase/firestore';
import { firestore } from './firebase';
import { ProfileComponentFollowing } from './ProfileComponentFollowing';
import { ShareCreditsPopup } from './ShareCreditsPopup';
import Modal from 'react-modal';
import { debounce } from 'lodash';
import { getFunctions, httpsCallable } from 'firebase/functions';

const MIN_PURCHASE_USD = 10;

const ProfilesDisplayShareCredits = ({ onProfilePress }) => {
  const [profiles, setProfiles] = useState([]);            // first batch of followers
  const [filteredProfiles, setFilteredProfiles] = useState([]); // list shown
  const [searchText, setSearchText] = useState('');

  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const isLoadingRef = useRef(false);
  const scrollerRef = useRef(null);

  const [usdInput, setUsdInput] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const {
    selectedUserIdShare,
    setShowSuccessAlertShareCredits,
    setCurrentlySharingCredits,
    showCreditSharePopup,
    setShowCreditSharePopup,
    followers,
    userId2,
    setSelectedUserIdShare,
    country,
    match,
    currentUser,
  } = useContext(AppContext);

  const functions = getFunctions();
  const createTokenCheckout = httpsCallable(functions, 'createPaymentLinkDatTokenAppsTestingWeb');
  const transferCredits = httpsCallable(functions, 'transferDatTokens');

  /* ───────────────────────────────────────── Load initial followers (9) */
  useEffect(() => {
    loadInitialProfiles();
  }, [followers]);

  useEffect(() => {
  const clearStripeState = () => {
    setPurchaseLoading(false);
    if (sessionStorage.getItem('stripeRedirect')) {
      sessionStorage.removeItem('stripeRedirect');
    }
  };
  const onPageShow = () => clearStripeState();
  const onVis = () => { if (!document.hidden) clearStripeState(); };

  if (sessionStorage.getItem('stripeRedirect')) clearStripeState();
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVis);
  return () => {
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVis);
  };
}, []);


  const loadInitialProfiles = async () => {
    if (!Array.isArray(followers)) return;
    setIsLoadingProfiles(true);

    const loaded = [];
    for (const uid of followers.slice(0, 9)) {
      if (uid === userId2) continue;
      const data = await getCachedUserDocMinimal(uid);
      if (data) loaded.push(data);
    }

    setProfiles(loaded);
    setFilteredProfiles(loaded);
    setIsLoadingProfiles(false);
  };

  /* ───────────────────────────────────────── Minimal user fetch */
  const getCachedUserDocMinimal = async (userId) => {
    const snap = await getDoc(doc(firestore, 'users', userId));
    if (!snap.exists()) return null;
    const fresh = snap.data();
    return {
      userId,
      voiceDropMuted: fresh.voiceDropMuted,
      accountType: fresh.accountType,
      name: fresh.name,
      username: fresh.username,
      profileImageUrl: fresh.profileImageUrl,
    };
  };

  /* ───────────────────────────────────────── Search (username_lower / artistNameLower) */
  const fetchProfilesFromFirestore = async (raw) => {
    const term = raw.replace(/^@/, '').toLowerCase().trim();
    if (!term) {
      setFilteredProfiles(profiles);
      return;
    }

    setIsLoadingProfiles(true);
    try {
      const q1 = query(
        collection(firestore, 'users'),
        where('username_lower', '>=', term),
        where('username_lower', '<=', term + '\uf8ff'),
        limit(8)
      );
      const q2 = query(
        collection(firestore, 'users'),
        where('artistNameLower', '>=', term),
        where('artistNameLower', '<=', term + '\uf8ff'),
        limit(8)
      );

      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const seen = new Set();
      const results = [];

      [...s1.docs, ...s2.docs].forEach((d) => {
        if (d.id === userId2) return;
        if (seen.has(d.id)) return;
        seen.add(d.id);
        results.push({ userId: d.id, ...d.data() });
      });

      setFilteredProfiles(results);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  const debounceFetch = useRef(debounce(fetchProfilesFromFirestore, 600)).current;
  const handleSearch = (text) => {
    setSearchText(text);
    debounceFetch(text);
  };

  /* ───────────────────────────────────────── Load more followers on scroll end */
  const loadMoreProfiles = async () => {
    if (isLoadingRef.current) return;
    if (!Array.isArray(followers)) return;
    if (profiles.length >= followers.length) return;

    isLoadingRef.current = true;
    setIsLoadingProfiles(true);

    const existing = new Set(profiles.map((p) => p.userId));
    const next = [];
    let idx = profiles.length;

    while (next.length < 9 && idx < followers.length) {
      const uid = followers[idx++];
      if (uid === userId2 || existing.has(uid)) continue;

      const data = await getCachedUserDocMinimal(uid);
      if (data) {
        existing.add(uid);
        next.push(data);
      }
    }

    setProfiles((prev) => [...prev, ...next]);
    if (!searchText.trim()) {
      setFilteredProfiles((prev) => [...prev, ...next]);
    }

    setIsLoadingProfiles(false);
    isLoadingRef.current = false;
  };

  const onScroller = (e) => {
    const el = e.currentTarget;
    // near end → load more
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 40) {
      loadMoreProfiles();
    }
  };

  /* ───────────────────────────────────────── Share Tokens (uses your existing popup) */
  const handleProfileSelect = async (profileData) => {
    const selectedUserId = profileData.userId;
    setSelectedUserIdShare(selectedUserId);
    setShowCreditSharePopup(true);
  };

  const handleShareCredits = async (amount) => {
    try {
      setCurrentlySharingCredits(true);
      setShowCreditSharePopup(false);
      await transferCredits({
        receiverId: selectedUserIdShare,
        amount, // amount of credits/tokens to transfer
      });
      setShowSuccessAlertShareCredits(true);
    } catch (err) {
      console.error('handleShareCredits: Error sharing credits:', err);
      alert('Failed to share. Please try again.');
    } finally {
      setCurrentlySharingCredits(false);
    }
  };

  /* ───────────────────────────────────────── Token Purchase (US-only unless match===true) */
const canShowPurchase = true; // gate removed: always show purchase card


  const numericUsd = useMemo(
    () => parseFloat(String(usdInput).replace(/[^\d.]/g, '')) || 0,
    [usdInput]
  );

  const handlePurchase = async () => {
    if (numericUsd < MIN_PURCHASE_USD) {
      alert(`Minimum $${MIN_PURCHASE_USD} purchase required.`);
      return;
    }
    if (!currentUser?.userId) {
      alert('Please sign in to purchase.');
      return;
    }
    try {
      setPurchaseLoading(true);
const { data } = await createTokenCheckout({ amountUsd: numericUsd });
if (!data?.url) throw new Error('Checkout link not returned.');

// same-tab redirect (match TokenBlockPage behavior)
sessionStorage.setItem('stripeRedirect', '1');
window.location.assign(data.url);

    } catch (err) {
      console.error('Checkout error', err);
      alert(err?.message || 'Failed to start checkout.');
    } finally {
      setPurchaseLoading(false);
    }
  };

  /* ───────────────────────────────────────── Render */
  return (
    <div style={styles.screen}>
      {/* faint background image like RN */}
      <img
        src={require('./assets/BlankBG.png')}
        alt=""
        style={styles.fullScreenBG}
      />

      <div style={styles.container}>
        {/* PURCHASE UTILITY TOKEN (US gate) */}
        {canShowPurchase && (
          <div style={styles.purchaseCard}>
            <div style={styles.sectionHeader}>Purchase Utility Token</div>

            <input
              style={styles.usdInput}
              placeholder="$ Amount (min $10)"
              value={usdInput}
              onChange={(e) => setUsdInput(e.target.value)}
            />

            <button
              style={{
                ...styles.purchaseButton,
                ...(numericUsd >= MIN_PURCHASE_USD ? null : { opacity: 0.6 }),
              }}
              onClick={handlePurchase}
              disabled={purchaseLoading || numericUsd < MIN_PURCHASE_USD}
            >
              {purchaseLoading ? 'Processing…' : 'Buy'}
            </button>
          </div>
        )}

        {/* SHARE TOKENS */}
        <div
          style={{
            ...styles.searchBlock,
            ...(!canShowPurchase ? styles.nonUSMargin : {}),
          }}
        >
          <div style={styles.sectionHeaderLeft}>Share Tokens</div>

          <input
            style={styles.searchInput}
            placeholder="Search..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />

          <div
            ref={scrollerRef}
            style={styles.scrollableContainer}
            onScroll={onScroller}
          >
            <div style={styles.horizontalList}>
              {filteredProfiles.map((profile) => (
                <ProfileComponentFollowing
                  key={profile.userId}
                  userProfile={profile}
                  onPress={() => handleProfileSelect(profile)}
                />
              ))}
              {isLoadingProfiles && (
                <div style={styles.loadingMore}>Loading…</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Tokens Modal (your existing popup) */}
      <Modal
        isOpen={showCreditSharePopup}
        onRequestClose={() => setShowCreditSharePopup(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10000,
          },
          content: {
            height: '400px',
            width: '100%',
            maxWidth: 560,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
          },
        }}
      >
        <div style={styles.sharingPost}>
          <ShareCreditsPopup onShare={handleShareCredits} />
        </div>
      </Modal>
    </div>
  );
};

/* ───────────────────────────────────────── Styles (web, RN look) */
const styles = {
  screen: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    backgroundColor: '#050008',
    overflow: 'hidden',
  },
  fullScreenBG: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.35,
    zIndex: 0,
    pointerEvents: 'none',
  },
  container: {
    position: 'relative',
    zIndex: 2,
    padding: '20px',
    paddingTop: '12vh',
    paddingBottom: '18vh',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    maxWidth: 980,
    margin: '0 auto',
  },

  /* Purchase card */
  purchaseCard: {
    width: '92%',
    margin: '0 auto',
    padding: '18px 16px',
    backgroundColor: '#0b0611',
    borderRadius: 12,
    border: '1px solid rgba(254,36,173,0.35)',
    boxShadow:
      '0 0 0 0 rgba(0,0,0,0.0), 0 0 12px rgba(164,16,232,0.25)',
  },
  sectionHeader: {
    color: '#fe24ad',
    fontSize: 20,
    fontFamily: 'brhendrix',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 0.3,
  },
  sectionHeaderLeft: {
    color: '#fe24ad',
    fontSize: 20,
    fontFamily: 'brhendrix',
    textAlign: 'left',
    marginBottom: 12,
    letterSpacing: 0.3,
  },
  usdInput: {
    width: '100%',
    border: '1px solid rgba(164,16,232,0.5)',
    backgroundColor: 'rgba(11,6,17,0.9)',
    borderRadius: 10,
    padding: '12px 14px',
    color: '#fff',
    fontSize: 18,
    fontFamily: 'brhendrix',
    textAlign: 'center',
    boxShadow: '0 0 10px rgba(164,16,232,0.15)',
  },
purchaseButton: {
  /* center horizontally */
  display: 'block',
  margin: '16px auto 0',

  backgroundColor: '#fe24ad',
  padding: '12px 30px',
  borderRadius: 10,
  border: '1px solid #a410e8',
  color: '#fff',
  fontSize: 18,
  fontFamily: 'brhendrix',
  cursor: 'pointer',
  boxShadow: '0 3px 12px rgba(164,16,232,0.35)',
},


  /* Search + horizontal list */
  searchBlock: {
    width: '100%',
    marginTop: '5vh',
  },
  nonUSMargin: {
    marginTop: '10vh',
  },
  searchInput: {
    alignSelf: 'center',
    width: '100%',
    borderColor: 'rgba(254,36,173,0.35)',
    backgroundColor: 'rgba(11,6,17,0.85)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 10,
    padding: '12px 14px',
    color: '#fff',
    fontSize: 18,
    fontFamily: 'brhendrix',
    marginBottom: 12,
    boxShadow: '0 0 10px rgba(254,36,173,0.15)',
  },
  scrollableContainer: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: 6,
  },
  horizontalList: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    padding: '1rem',
    alignItems: 'stretch',
  },
  loadingMore: {
    color: '#62fbfa',
    fontFamily: 'brhendrix',
    display: 'flex',
    alignItems: 'center',
  },

  /* Modal body for share popup */
  sharingPost: {
    width: '100%',
    maxWidth: 520,
    height: 200,
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
  },
};

export default ProfilesDisplayShareCredits;
